import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DateTime } from "luxon";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";

interface RedactionHistoryEntry {
  id: string;
  eventType: string;
  occurredAt: string;
  actorId: string;
  actorName: string;
  actorEmail: string;
  reason: string | null;
  details: Record<string, unknown> | null;
}

interface RedactionHistoryResponse {
  participantId: string;
  entries: RedactionHistoryEntry[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  participantId: string;
  /**
   * Current redaction status of the participant — drives which entry is
   * surfaced as the "latest matching event" in the prominent block.
   */
  currentStatus: "active" | "deactivated" | "redacted" | "deleted";
}

const EVENT_LABELS: Record<string, string> = {
  "participant_redaction.deactivated": "Deactivated",
  "participant_redaction.reactivated": "Reactivated",
  "participant_redaction.study_redacted": "Study redacted",
  "participant_redaction.registry_redacted": "Registry redacted",
  "participant_redaction.revived": "Revived",
  "participant_redaction.revival_override": "Revival override (post-grace-period)",
  "participant_redaction.hard_deleted": "Hard deleted",
  "participant_redaction.escrow_updated": "Escrow updated",
};

const EVENT_COLORS: Record<
  string,
  "default" | "warning" | "error" | "success" | "info"
> = {
  "participant_redaction.deactivated": "warning",
  "participant_redaction.reactivated": "success",
  "participant_redaction.study_redacted": "error",
  "participant_redaction.registry_redacted": "error",
  "participant_redaction.revived": "success",
  "participant_redaction.revival_override": "info",
  "participant_redaction.hard_deleted": "error",
  "participant_redaction.escrow_updated": "info",
};

const STATUS_TO_PRIMARY_EVENTS: Record<string, string[]> = {
  redacted: [
    "participant_redaction.registry_redacted",
    "participant_redaction.study_redacted",
  ],
  deactivated: ["participant_redaction.deactivated"],
  deleted: ["participant_redaction.hard_deleted"],
  active: ["participant_redaction.reactivated", "participant_redaction.revived"],
};

const formatDate = (iso: string): string => {
  const d = DateTime.fromISO(iso);
  return d.isValid ? d.toFormat("MM/dd/yyyy HH:mm") : iso;
};

const EntryBlock: React.FC<{ entry: RedactionHistoryEntry; prominent?: boolean }> = ({
  entry,
  prominent,
}) => {
  const label = EVENT_LABELS[entry.eventType] ?? entry.eventType;
  const color = EVENT_COLORS[entry.eventType] ?? "default";
  const bulkOperationId =
    (entry.details?.bulkOperationId as string | null | undefined) ?? null;

  return (
    <Box
      sx={{
        p: prominent ? 2 : 1.5,
        mb: 1.5,
        bgcolor: prominent ? "grey.100" : "background.paper",
        border: 1,
        borderColor: prominent ? "primary.light" : "divider",
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Chip label={label} size="small" color={color} />
        <Typography variant="caption" color="text.secondary">
          {formatDate(entry.occurredAt)}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        <strong>By:</strong> {entry.actorName || "(unknown)"}
        {entry.actorEmail ? ` <${entry.actorEmail}>` : ""}
      </Typography>
      {entry.reason ? (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
          <strong>Reason:</strong> {entry.reason}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          No reason recorded (legacy entry)
        </Typography>
      )}
      {bulkOperationId && (
        <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
          <Chip label="Bulk operation" size="small" variant="outlined" color="info" />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
            title="Participants redacted together in this batch share this id"
          >
            {bulkOperationId}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const RedactionHistoryModal: React.FC<Props> = ({
  open,
  onClose,
  participantId,
  currentStatus,
}) => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<RedactionHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setShowHistory(false);
      try {
        const res = await http.get(
          `/admin/participants/${participantId}/redaction-history`,
        );
        const data = res?.data?.data as RedactionHistoryResponse | undefined;
        if (!cancelled) setEntries(data?.entries ?? []);
      } catch (err) {
        if (!cancelled) {
          setEntries([]);
          errorToastMessage(err as Error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, participantId]);

  const primaryEventTypes = STATUS_TO_PRIMARY_EVENTS[currentStatus] ?? [];
  const latestMatching = entries.find((e) =>
    primaryEventTypes.includes(e.eventType),
  );
  const olderEntries = entries.filter((e) => e.id !== latestMatching?.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Redaction history</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && entries.length === 0 && (
          <Alert severity="info">
            No redaction history recorded for this participant yet.
          </Alert>
        )}

        {!loading && entries.length > 0 && (
          <>
            {latestMatching ? (
              <>
                <Typography variant="overline" color="text.secondary">
                  Latest matching the current state ({currentStatus})
                </Typography>
                <EntryBlock entry={latestMatching} prominent />
              </>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No event matching the current state ({currentStatus}). See full
                history below.
              </Alert>
            )}

            {olderEntries.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Button
                  variant="text"
                  size="small"
                  endIcon={
                    showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  }
                  onClick={() => setShowHistory((s) => !s)}
                >
                  {showHistory
                    ? "Hide history"
                    : `View history (${olderEntries.length})`}
                </Button>
                <Collapse in={showHistory}>
                  <Box sx={{ mt: 1 }}>
                    {olderEntries.map((entry) => (
                      <EntryBlock key={entry.id} entry={entry} />
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RedactionHistoryModal;
